import * as React from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { AgCharts } from "ag-charts-react";
// import fileDownload from "js-file-download";
// import { uppercaseFirstLetter } from "components/utils";
import {
  Space,
  Spin,
  Descriptions,
  message,
  Table,
  Skeleton,
  Progress,
  Form,
  Button,
  Modal,
  Upload,
  Popconfirm,
  Checkbox,
  Badge,
  Tooltip,
} from "antd";

import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";

import { formatFullDateFromTimeStamp } from "api/utils";
import {
  MLModelApiCommunicator,
  PredictionApiCommunicator,
  UploadCommunicator,
  useAmplifyOrgSubscription,
} from "api";

import { ListPageLayout } from "layouts";
import ReactPlayer from "react-player";
import { TableListCellItems } from "components/Model/TableListCellItems";
/**
 * MLModelResultDetailPage Component
 * 
 * Displays detailed information about a specific ML model training result.
 * Provides functionality for viewing training metrics, downloading model files,
 * creating predictions, and managing result data.
 * 
 * Features:
 * - Complete ML model result information display
 * - Training metrics visualization with charts
 * - Model file downloads (checkpoint, events, hparams, model)
 * - Video playback for training visualization
 * - Prediction creation and management
 * - File upload capabilities
 * - Real-time updates via Amplify subscriptions
 * - Loss chart data visualization
 * - Published status management
 * 
 * @returns {JSX.Element} The ML model result detail page component
 */
export function MLModelResultDetailPage() {
  const { mlmodelId, resultId } = useParams();
  return (
    <MLModelResultFetchResults mlmodelId={mlmodelId} resultId={resultId} />
  );
}

/**
 * MLModelResultFetchResults Component
 * 
 * Internal component that handles the main ML model result detail functionality including
 * data fetching, state management, and user interactions. This component manages
 * all the complex state and API interactions for the ML model result detail view.
 * 
 * @param {Object} props - Component props
 * @param {string} props.mlmodelId - The ID of the ML model
 * @param {string} props.resultId - The ID of the specific result to display
 * @returns {JSX.Element} The ML model result fetch results component
 */
function MLModelResultFetchResults({ mlmodelId, resultId }) {
  const [uploadCommunicator] = React.useState(new UploadCommunicator());
  const [uploadingPercent, setUploadingPercent] = React.useState(0);
  const [uploading, setUploading] = React.useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [mlModelApiCommunicator] = React.useState(new MLModelApiCommunicator());
  const [predictionApiCommunicator] = React.useState(
    new PredictionApiCommunicator()
  );
  const [loading, setLoading] = React.useState(false);
  const [loadingMessage] = React.useState("Loading...");
  const [mlModel, setMLModel] = React.useState({});
  const [createPredictionModalIsOpen, openCreatePredictionModal] =
    React.useState(false);
  const [pendingDeleteId, setPendingDeleteId] = React.useState("");

  const mlModelResult = React.useMemo(() => {
    return (mlModel?.results || []).find(
      (result) => result.resultId === resultId
    );
  }, [resultId, mlModel]);

  // Ml Model Data
  const mlModelName = mlModel?.mlModelName;
  const mlModelDateAdded = mlModel?.dateAdded;
  // Ml Model Result
  const mlModelResultVersion = mlModelResult?.version;
  const checkPointURL = mlModelResult?.files?.checkpoint;
  const eventsURL = mlModelResult?.files?.events;
  const hparamsURL = mlModelResult?.files?.hparams;
  const modelURL = mlModelResult?.files?.model;
  const lossChartURL = mlModelResult?.files?.losschart;
  const pwamModelVersion = mlModelResult?.options?.pwamModelVersion || "m003";
  const maxEpochs = mlModelResult?.options?.maxEpochs || "2000";
  const publishedStatus = mlModelResult?.published || false;
  // The URLs nested on the MLModelResult are signed by S3
  // so, while the "video" itself is static, updates triggered
  // via AppSync to the current user's instance will unnecessarily refresh
  // the video as the URL is perceived as unique.
  // We manually set and unset these on React refs which persist between render cycles
  // to circumvent this issue.
  const partMp4Ref = React.useRef();
  const validationMp4Ref = React.useRef();
  const [lossData, setLossData] = React.useState();

  const errorToast = React.useCallback(
    (text) => messageApi.error(text),
    [messageApi]
  );

  const fetchPageData = React.useCallback(async () => {
    setLoading(true);
    validationMp4Ref.current = "";
    partMp4Ref.current = "";
    try {
      console.log("fetching mlmodel %s result %s", mlmodelId, resultId);
      const res = await mlModelApiCommunicator.fetchResult(mlmodelId, resultId);
      // sort and set
      setMLModel(res.data);
      // set the video URLs initially
      const mlModelRes = (res.data?.results || []).find(
        (r) => r.resultId === resultId
      );

      if (mlModelRes) {
        validationMp4Ref.current = mlModelRes?.files?.validationMp4;
        partMp4Ref.current = mlModelRes?.files?.partMp4;
        if (mlModelRes?.files?.lossjson) {
          const lossDataUrl = mlModelRes?.files?.lossjson;
          const response = await axios.get(lossDataUrl);
          setLossData(response.data);
          console.log("Loss data loaded:", response.data);
        }

        
      }
    } catch (err) {
      messageApi.error(err.message);
    }
    setLoading(false);
  }, [mlmodelId, mlModelApiCommunicator, resultId, messageApi]);

  useAmplifyOrgSubscription((update) => {
    // console.log("UPDATE", update);
    if (update.type === "mlmodel" && !loading) {
      setMLModel((currModel) =>
        currModel.mlModelId === update.data.mlModelId ? update.data : currModel
      );
    }
    if(update.type === "prediction" && !loading) {
      if(update.data.mlModelResultId === resultId) {
        console.log("Got resultId prediction", update.data);
        //Update mlModel results[0] predictions
        setMLModel((currModel) => {
          const newResults = currModel.results.map((r) => {
            if(r.resultId === resultId) {
              //Only update results[0].predictions with matching update.data.predictionId
              r.predictions = r.predictions.map(prediction =>
                prediction.predictionId === update.data.predictionId ? update.data : prediction
              );

            }
            return r;
          });
          return {...currModel, results: newResults};
        }); 
      }
    }
  });

  // Fetch details on mount
  React.useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const updateUploadProgress = React.useCallback(
    async (progressEvent) => {
      if (progressEvent.event.lengthComputable) {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadingPercent(percent);
      }
    },
    [setUploadingPercent]
  );

  const uploadFile = React.useCallback(
    async (values) => {
      const file = values.upload.file.originFileObj;
      openCreatePredictionModal(false);
      setUploadingPercent(0);
      setUploading(true);
      setLoading({ active: true, message: "Uploading: " + file.name });

      try {
        const predictionOptions = { wam: values.wam ? values.wam : false };
        console.log("predictionOptions", predictionOptions);
        const createRes = await predictionApiCommunicator.create(
          mlmodelId,
          resultId,
          file.name,
          predictionOptions
        );
        // From the data returned use gcPrintProjectUploadPath to upload the file
        await uploadCommunicator.uploadFile(
          createRes.data.uploadUrl,
          file,
          updateUploadProgress
        );
        setLoading((s) => ({ ...s, message: "Finishing Upload..." }));
      } catch (e) {
        setLoading(false);
        errorToast("Error uploading file " + file.name);
      }

      setTimeout(() => {
        setUploading(false);
        setUploadingPercent(0);
        fetchPageData();
        setLoading(false);
      }, 1000);
    },
    [
      mlmodelId,
      resultId,
      uploadCommunicator,
      updateUploadProgress,
      fetchPageData,
      errorToast,
      predictionApiCommunicator,
    ]
  );

  const cancelDelete = React.useCallback(() => {
    setPendingDeleteId("");
  }, []);

  const confirmDelete = React.useCallback(
    async (predictionId) => {
      setPendingDeleteId("");
      // setLoading(true);
      if (!mlModelResult) {
        return;
      }
      try {
        const predictionIndex = mlModelResult.predictions.findIndex(
          (p) => p.predictionId === predictionId
        );
        if (predictionIndex !== -1) {
          mlModelResult.predictions.splice(predictionIndex, 1);
        }

        await predictionApiCommunicator.delete(predictionId);
        fetchPageData();
      } catch (err) {
        messageApi.error("Error deleting prediction " + err.message);
      }
      // setLoading(false);
    },
    [mlModelResult, predictionApiCommunicator, fetchPageData, messageApi]
  );

  const predictionColumns = React.useMemo(
    () => [
      {
        title: "File",
        dataIndex: "fileName",
        key: "fileName",
        render: (_, p) => {
          try {
            if (!p.files?.userInput) {
              if(p.fileName) {
                return p.fileName;
              } else {
                return "N/A";
              }
            } else {
              const url = new URL(p.files.userInput);
              const filename = url.pathname.split("/").pop();
              return decodeURIComponent(filename);
            }
          } catch (e) {
            return "Invalid URL";
          }
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (_, r) => (
          r.status === "failed" ? (
            <Tooltip title={r.message}>
              <span>
                <TableListCellItems.StatusBadge status={r.status} />
              </span>
            </Tooltip>
          ) : (
            <TableListCellItems.StatusBadge status={r.status} />
          )
        ),
      },
      {
        title: "WAM Included",
        key: "wamIncluded",
        render: (_, r) => {
          return r.options?.wam ? "Yes" : "No";
        },
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        defaultSortOrder: 'descend',
        render: (timestamp) =>
          timestamp ? formatFullDateFromTimeStamp(timestamp) : "N/A",
      },
      // {
      //   title: "Started",
      //   dataIndex: "startedAt",
      //   key: "startedAt",
      //   render: (timestamp) =>
      //     timestamp ? formatFullDateFromTimeStamp(timestamp) : "N/A",
      // },
      {
        title: "Completed",
        dataIndex: "completedAt",
        key: "resultCompletedAt",
        sorter: (a, b) => new Date(a.completedAt) - new Date(b.completedAt),
        render: (timestamp) =>
          timestamp ? formatFullDateFromTimeStamp(timestamp) : "N/A",
      },
      {
        title: "Action",
        key: "action",
        render: (_, r) => (
          <Space>
            <Popconfirm
              title="Delete this prediction?"
              description="Are you sure to delete this prediction? This action cannot be undone."
              open={r.predictionId === pendingDeleteId}
              onConfirm={() => confirmDelete(r.predictionId)}
              onCancel={cancelDelete}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined
                onClick={() => setPendingDeleteId(r.predictionId)}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [confirmDelete, cancelDelete, pendingDeleteId]
  );

  const downloadAsset = React.useCallback(
    async (assetKey) => {
      try {
        const res = await mlModelApiCommunicator.fetchResult(
          mlmodelId,
          resultId
        );
        if (res?.data?.results?.[0]?.files?.[assetKey]) {
          const assetUrl = res.data.results[0].files[assetKey];
          window.open(assetUrl, "_blank");
        } else {
          throw new Error("Asset not found");
        }
      } catch (err) {
        messageApi.error("Error downloading asset " + err.message);
      }
    },
    [messageApi, mlModelApiCommunicator, mlmodelId, resultId]
  );

  const publishResult = React.useCallback(async () => {
    try {
      setLoading(true);
      await mlModelApiCommunicator.publishResult(mlmodelId, resultId);
      fetchPageData();
    } catch (err) {
      setLoading(false);
      messageApi.error("Error publishing result " + err.message);
    }
  }, [fetchPageData, messageApi, mlModelApiCommunicator, mlmodelId, resultId]);

  return (
    <ListPageLayout>
      {contextHolder}
      {uploading && <Progress percent={uploadingPercent} />}
      {loading ? (
        <span>
          <Spin /> {loadingMessage}
        </span>
      ) : (
        <MLModelResultDetails
          mlModelId={mlmodelId}
          mlModelName={mlModelName}
          mlModelDateAdded={mlModelDateAdded}
          mlModelResultVersion={mlModelResultVersion}
          checkPointURL={checkPointURL}
          eventsURL={eventsURL}
          hparamsURL={hparamsURL}
          modelURL={modelURL}
          lossChartURL={lossChartURL}
          pwamModelVersion={pwamModelVersion}
          partMp4={partMp4Ref.current}
          validationMp4={validationMp4Ref.current}
          lossData={lossData}
          maxEpochs={maxEpochs}
          downloadAsset={downloadAsset}
          publishedStatus={publishedStatus}
          publishResult={publishResult} 
          versionStatus={mlModelResult?.status}
          versionMessage={mlModelResult?.message}
        />
      )}
      <Skeleton loading={loading}>
        <Table
          style={{ marginTop: "32px" }}
          columns={predictionColumns}
          dataSource={mlModelResult?.predictions}
          rowKey="predictionId"
          title={() => {
            return (
              <Space
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3>Predictions</h3>
                <Button
                  type="primary"
                  onClick={() => openCreatePredictionModal(true)}
                >
                  Add Prediction
                </Button>
              </Space>
            );
          }}
          expandable={{
            expandedRowRender: (record) => {
              // Define columns for the table
              const columnTranslation = {
                compareCad: "Compared to CAD",
                compareScan: "Compared to Scan",
                userInput: "Uploaded File",
                predictedSSM: "Predicted SSM",
                predictedScan: "Predicted Scan",
                wam: "WAM",
              };

              const columns = [
                {
                  title: "Type",
                  dataIndex: "key",
                  key: "key",
                  render: (key) => {
                    return columnTranslation[key] || key;
                  },
                },
                {
                  title: "File",
                  dataIndex: "value",
                  key: "value",
                  render: (fileUrl) => {
                    // Extract the file name from the URL
                    const fileName = new URL(fileUrl).pathname.split("/").pop();
                    return (
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {fileName}
                      </a>
                    );
                  },
                },
              ];

              // Transform record.files into an array of objects with key and value
              const data = record.files
                ? Object.entries(record.files).map(([key, value]) => ({
                    key,
                    value,
                  }))
                : [];

              return (
                <Table columns={columns} dataSource={data} pagination={false} />
              );
            },
            rowExpandable: (record) =>
              record.status &&
              (record.status === "completed" || record.status === "complete"),
          }}
        />
      </Skeleton>
      <CreatePredictionModal
        open={createPredictionModalIsOpen}
        onCancel={() => openCreatePredictionModal(false)}
        onSubmit={uploadFile}
      />
    </ListPageLayout>
  );
}

function CreatePredictionModal({ open = false, onCancel, onSubmit }) {
  const [form] = Form.useForm();
  const [fileList, setFileList] = React.useState([]);
  const onFileChange = React.useCallback(
    (fs) => {
      // manually set status for antd's rendering
      for (const f of fs.fileList) {
        f.status = "done";
      }
      setFileList([...fs.fileList]);
      // Set modelName to the name of the first file
      // if (fs.fileList[0]) {
      //   form.setFieldsValue({ modelName: fs.fileList[0].name });
      // }
    },
    [setFileList]
  );
  return (
    <Modal
      title="Prediction Print File"
      okText="Add"
      open={open}
      onCancel={onCancel}
      afterOpenChange={(open) => {
        if (!open) {
          form.resetFields();
          setFileList([]);
        }
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            onSubmit(values);
            form.resetFields();
          })
          .catch((info) => {
            console.log("validation failed", info);
          });
      }}
    >
      <Space direction="vertical" style={{ display: "flex" }}>
        <Form form={form} layout="vertical" initialValues={{}}>
          <Form.Item
            label="Prediction File Upload (.print or .3mf)"
            name="upload"
            rules={[{ required: true, message: "file is required" }]}
          >
            <Upload
              accept=".print,.3mf"
              maxCount={1}
              customRequest={() => {}}
              onChange={onFileChange}
              fileList={fileList}
            >
              <Button icon={<UploadOutlined />}>Select Print File</Button>
            </Upload>
          </Form.Item>
          <Form.Item label="" name="wam" valuePropName="checked">
            <Checkbox>Generate WAM</Checkbox>
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
}

function splitURLToName(url) {
  return url ? new URL(url).pathname.split("/").pop() : "";
}

function MLModelResultDetails(props) {
  const {
    mlModelId,
    mlModelName,
    mlModelDateAdded,
    mlModelResultVersion,
    checkPointURL,
    eventsURL,
    hparamsURL,
    modelURL,
    partMp4,
    validationMp4,
    lossData,
    pwamModelVersion,
    maxEpochs,
    downloadAsset,
    publishedStatus,
    publishResult,
    versionStatus,
    versionMessage,
  } = props;

  const checkPointFileName = splitURLToName(checkPointURL);
  const eventsFileName = splitURLToName(eventsURL);
  const hparamsFileName = splitURLToName(hparamsURL);
  const modelFileName = splitURLToName(modelURL);

  const chartAxes = [
    {
      type: "number",
      position: "bottom",
      title: {
        text: "Index",
      },
    },
    {
      type: "number",
      position: "left",
      title: {
        text: "Value",
      },
    },
  ];

  const validationLossData = lossData?.validation_loss
    ? lossData?.validation_loss.map((value, index) => ({
        step: index,
        value: value,
      }))
    : [];

  const trainLossData = lossData?.train_loss
    ? lossData?.train_loss.map((value, index) => ({
        step: index,
        value: value,
      }))
    : [];

  const chartSeries = [{ type: "line", xKey: "step", yKey: "value" }];

  console.log({
    trainLossData,
    validationLossData,
  });

  return (
    <Descriptions
      column={3}
      bordered
      items={[
        {
          key: "model-name",
          label: "Model Name",
          children: mlModelName,
        },
        {
          key: "date-added",
          label: "Date Added",
          children: formatFullDateFromTimeStamp(mlModelDateAdded),
          span: 2,
        },
        {
          key: "version-status",
          label: "Status",
          children: (
            versionStatus === "failed" ? (
              <Tooltip title={versionMessage}>
                <span>
                  <TableListCellItems.StatusBadge status={versionStatus} />
                </span>
              </Tooltip>
            ) : (
              <TableListCellItems.StatusBadge status={versionStatus} />
            )
          ),
        },
        {
          key: "result-version",
          label: "Result Version",
          children: mlModelResultVersion,
          span: 2,
        },
        {
          key: "published-status",
          label: "Published",
          children: publishedStatus ? (
            <Badge status='success' text='Yes' />
          ) : (
            <>
              No 
              {versionStatus === "complete" && (
              <Popconfirm
                title={
                  <>
                    Any previously published versions will be unpublished.
                    <br />
                    Are you sure you want to publish this result?
                  </>
                }
                onConfirm={publishResult}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="link"
                  style={{ marginLeft: '2px' }}
                >
                  publish
                </Button>
              </Popconfirm>
              )}
            </>
          ),
        },
        {
          key: "pwam-model-version",
          label: "PWAM Model Version",
          children: pwamModelVersion,
          span: 2,
        },
        {
          key: "pwam-max-epochs",
          label: "PWAM Max Epochs",
          children: maxEpochs,
          span: 3,
        },
        {
          key: "checkpoint-file",
          label: "Checkpoint File",
          span: 3,
          children: (
            <Button type="link" onClick={() => downloadAsset("checkpoint")}>
              {checkPointFileName}
            </Button>
          ),
        },
        {
          key: "events-file",
          label: "Events File",
          span: 3,
          children: (
            <Button type="link" onClick={() => downloadAsset("events")}>
              {eventsFileName}
            </Button>
          ),
        },
        {
          key: "hparams-file",
          label: "HParams File",
          span: 3,
          children: (
            <Button type="link" onClick={() => downloadAsset("hparams")}>
              {hparamsFileName}
            </Button>
          ),
        },
        {
          key: "model-file",
          label: "ML Model File",
          span: 3,
          children: (
            <Button type="link" onClick={() => downloadAsset("model")}>
              {modelFileName}
            </Button>
          ),
        },
        {
          key: "train-loss-chart",
          label: "Training Loss",
          children: (
            <AgCharts
              options={{
                data: trainLossData,
                series: chartSeries,
                axes: chartAxes,
              }}
            />
          ),
        },
        {
          key: "validation-loss-chart",
          label: "Validation Loss",
          span: 3,
          children: (
            <AgCharts
              options={{
                data: validationLossData,
                series: chartSeries,
                axes: chartAxes,
              }}
            />
          ),
        },
        // {
        //   key: "loss-chart",
        //   label: "Loss Chart",
        //   span: 4,
        //   children: (
        //     lossData === undefined && lossChartURL && (
        //       <img
        //         src={lossChartURL}
        //         alt="Loss Chart"
        //         style={{ width: '70%', height: '70%', objectFit: 'contain' }}
        //       />
        //     )
        //   ),
        // },
        {
          key: "part-video",
          label: "Part Video",
          children: <MemoizedReactPlayer url={partMp4} />,
        },
        {
          key: "validation-video",
          label: "Validation Video",
          children: <MemoizedReactPlayer url={validationMp4} />,
        },
      ]}
      title={
        <div>
          <h3 style={{ paddingLeft: "16px", display: "inline-block" }}>
            ML Model -
          </h3>
          <span>
            {" "}
            <Link to={`/mlmodels/${mlModelId}`}>{mlModelName}</Link>
          </span>
          <span> - {mlModelResultVersion}</span>
        </div>
      }
    />
  );
}

const MemoizedReactPlayer = React.memo(function (props) {
  const { url } = props;
  return (
    <ReactPlayer
      loop
      url={url}
      controls={true}
      width="100%" // You can adjust this as needed
    />
  );
});
